import React from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { SingleModeStats, VsModeStats, DailyChallengeStats, DifficultyBreakdown } from '../PlayerHandler';

type StatsMode = 'single' | 'vs' | 'vsBot' | 'daily';

interface PlayerStatsSectionProps {
  mode: StatsMode;
  title: string;
  singleStats?: SingleModeStats;
  vsStats?: VsModeStats;
  dailyStats?: DailyChallengeStats;
}

const formatTime = (seconds: number | null | undefined): string => {
  if (seconds == null || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const WinLossBar: React.FC<{ wins: number; losses: number; theme: any }> = ({ wins, losses, theme }) => {
  const total = wins + losses;
  if (total === 0) return <div style={{ color: theme.textSecondary, fontSize: '13px' }}>No games</div>;
  const winPct = Math.round((wins / total) * 100);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
        <span style={{ color: '#22c55e', fontWeight: 600 }}>Wins {wins}</span>
        <span style={{ fontWeight: 600, color: theme.text }}>{winPct}%</span>
        <span style={{ color: '#ef4444', fontWeight: 600 }}>Losses {losses}</span>
      </div>
      <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', background: theme.border }}>
        <div style={{ width: `${winPct}%`, background: '#22c55e', transition: 'width 0.3s' }} />
        <div style={{ width: `${100 - winPct}%`, background: '#ef4444', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string | number; color?: string; theme: any }> = ({ label, value, color, theme }) => (
  <div style={{ textAlign: 'center', padding: '8px 6px', background: theme.tableHeader, borderRadius: '6px' }}>
    <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '16px', fontWeight: 700, color: color || theme.text }}>{value}</div>
  </div>
);

const DifficultyTable: React.FC<{ data: DifficultyBreakdown[]; theme: any }> = ({ data, theme }) => {
  const filteredData = data.filter(d => d.gamesPlayed && d.gamesPlayed > 0);
  if (filteredData.length === 0) return null;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr>
            {['', 'Avg Errors', 'Avg Hints', 'Best Time', 'Avg Time', 'Worst Time'].map(h => (
              <th key={h} style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600, color: theme.textSecondary, borderBottom: `1px solid ${theme.border}`, background: theme.tableHeader }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredData.map(row => (
            <tr key={row.difficulty}>
              <td style={{ padding: '5px 8px', fontWeight: 600, color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.difficulty}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center', color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.avgErrors}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center', color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.avgHints}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center', color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{formatTime(row.bestTime)}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center', color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{formatTime(row.avgTime)}</td>
              <td style={{ padding: '5px 8px', textAlign: 'center', color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{formatTime(row.worstTime)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PlayerStatsSection: React.FC<PlayerStatsSectionProps> = ({ mode, title, singleStats, vsStats, dailyStats }) => {
  const { theme, isMobile } = useTheme();

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '10px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '10px',
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '10px',
      flexWrap: 'wrap' as const,
    },
    titleText: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
    },
    titleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      color: theme.textSecondary,
    },
    titleBadgeValue: {
      fontSize: '16px',
      fontWeight: 700,
      padding: '2px 10px',
      borderRadius: '6px',
      border: `1px solid ${theme.border}`,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: '8px',
      marginBottom: '10px',
    },
    statsGridSmall: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: '8px',
      marginTop: '10px',
    },
    empty: {
      color: theme.textSecondary,
      fontSize: '14px',
      fontStyle: 'italic' as const,
    },
  };

  if (mode === 'single' && singleStats) {
    const total = singleStats.gamesAttempted ?? 0;
    const completed = singleStats.gamesCompleted ?? 0;
    const failed = singleStats.gamesFailed ?? 0;
    const successRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return (
      <div style={styles.container}>
        <div style={styles.titleRow}>
          <span style={styles.titleText}>{title}</span>
          <span style={styles.titleBadge}>Games Attempted <span style={{ ...styles.titleBadgeValue, color: theme.text }}>{total}</span></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '2px' }}>Success Rate</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#9ca3af', marginBottom: '4px' }}>{successRate}%</div>
            <WinLossBar wins={completed} losses={failed} theme={theme} />
          </div>
          <DifficultyTable data={singleStats.byDifficulty || []} theme={theme} />
        </div>
      </div>
    );
  }

  if ((mode === 'vs' || mode === 'vsBot') && vsStats) {
    const gamesPlayed = vsStats.gamesPlayed ?? 0;
    if (gamesPlayed === 0) {
      return (
        <div style={styles.container}>
          <div style={styles.title}>{title}</div>
          <div style={styles.empty}>No games played</div>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        <div style={styles.titleRow}>
          <span style={styles.titleText}>{title}</span>
          <span style={styles.titleBadge}>Games Played <span style={{ ...styles.titleBadgeValue, color: theme.text }}>{gamesPlayed}</span></span>
          <span style={styles.titleBadge}>Highest Score <span style={{ ...styles.titleBadgeValue, color: '#22c55e' }}>{vsStats.highestScore ?? 0}</span></span>
          <span style={styles.titleBadge}>Lowest Score <span style={{ ...styles.titleBadgeValue, color: '#ef4444' }}>{vsStats.lowestScore ?? 0}</span></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '2px' }}>WIN / Loss</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#22c55e', marginBottom: '4px' }}>{((vsStats.wins ?? 0) / Math.max(gamesPlayed, 1) * 100).toFixed(1)}%</div>
            <WinLossBar wins={vsStats.wins ?? 0} losses={vsStats.losses ?? 0} theme={theme} />
          </div>
          <div style={styles.statsGridSmall}>
            <StatCard label="Avg Score" value={vsStats.avgScore ?? 0} theme={theme} />
            <StatCard label="Avg Hints Used" value={vsStats.avgHints ?? 0} theme={theme} />
            <StatCard label="Avg Errors" value={vsStats.avgErrors ?? 0} theme={theme} />
            <StatCard label="Avg Inputs" value={vsStats.avgInputs ?? 0} theme={theme} />
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'daily' && dailyStats) {
    const gamesPlayed = dailyStats.gamesPlayed ?? 0;
    if (gamesPlayed === 0) {
      return (
        <div style={styles.container}>
          <div style={styles.title}>{title}</div>
          <div style={styles.empty}>No games played</div>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        <div style={styles.titleRow}>
          <span style={styles.titleText}>{title}</span>
          <span style={styles.titleBadge}>Games Played <span style={{ ...styles.titleBadgeValue, color: theme.text }}>{gamesPlayed}</span></span>
          <span style={styles.titleBadge}>Best Time <span style={{ ...styles.titleBadgeValue, color: '#22c55e' }}>{formatTime(dailyStats.bestTime)}</span></span>
          <span style={styles.titleBadge}>Lowest Time <span style={{ ...styles.titleBadgeValue, color: '#ef4444' }}>{formatTime(dailyStats.worstTime)}</span></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '2px' }}>WIN / Loss</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#22c55e', marginBottom: '4px' }}>{((dailyStats.gamesCompleted ?? 0) / Math.max(gamesPlayed, 1) * 100).toFixed(0)}%</div>
            <WinLossBar wins={dailyStats.gamesCompleted ?? 0} losses={dailyStats.gamesFailed ?? 0} theme={theme} />
          </div>
          <div style={styles.statsGridSmall}>
            <StatCard label="Avg Score" value={dailyStats.avgScore ?? 0} theme={theme} />
            <StatCard label="Avg Hints Used" value={dailyStats.avgHints ?? 0} theme={theme} />
            <StatCard label="Avg Errors" value={dailyStats.avgErrors ?? 0} theme={theme} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.title}>{title}</div>
      <div style={styles.empty}>No data available</div>
    </div>
  );
};

export default PlayerStatsSection;
